import React, { useEffect } from "react";
import {
  Navigate,
  NavigateFunction,
  Params,
  Route,
  Routes,
} from "react-router-dom";
import MerchantAccountStore from "./MerchantAccountStore";
import { observer } from "mobx-react";
import {
  CustomButton,
  FlexContainer,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { ItemsStore, RolesAndPermissionStore } from "../../shared/store";
import { SelectedItemModel } from "../../shared/models";
import { DashboardTopBar, NotificationBox } from "../../shared/components";
import { DashboardGrid } from "../../shared/styles";
import NoMerchantAccountPlaceholder from "./components/NoMerchantAccountPlaceholder";

const MerchantApplicationSuccess = React.lazy(
  () => import("./components/MerchantApplicationSuccess")
);
const MerchantAccountSignUp = React.lazy(
  () => import("./components/MerchantAccountSignUp")
);
const MerchantAccounts = React.lazy(
  () => import("./components/MerchantAccounts")
);
const ItemContentArea = React.lazy(
  () => import("../../shared/components/ItemContentArea")
);
const MerchantAccountDetail = React.lazy(
  () => import("./components/MerchantAccountDetail")
);

interface IProps {
  location: Location;
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  params: Readonly<Params<string>>;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  getMerchantAccounts: () => void;
}
const MerchantAccountRoutes: React.FC<IProps> = ({
  location,
  itemStore,
  navigate,
  merchantAccountStore,
  params,
  rolesAndPermissionStore,
  getMerchantAccounts,
}) => {
  function merchantAccountId() {
    const split = ((params && params["*"]) || "").split("/");
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  useEffect(() => {
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: "",
      })
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);

  function topBar(
    node: React.ReactNode,
    rightContent: React.ReactNode,
    showNoMerchantAccountNotification?: boolean
  ) {
    return (
      <>
        <DashboardTopBar
          showNoMerchantAccountNotification={showNoMerchantAccountNotification}
          itemStore={itemStore}
          rightContent={rightContent}
          showRightContent={
            rightContent && showNoMerchantAccountNotification !== true
          }
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          onlyShowNotificationBox={showNoMerchantAccountNotification === true}
          showMerchantAccountNotificationBar={showNoMerchantAccountNotification}
        />
      </>
    );
  }

  function topRightButton() {
    return (
      <FlexContainer alignItems="center" spacing={20} justifyContent="flex-end">
        {/* <CustomButton
          color="#556bb8"
          bgColor="#fff"
          height="40px"
          borderColor="#556bb8"
          disabled={true}
          // onClick={() => {
          //     navigate('/app/merchant-accounts/create-new');
          // }}
        >
          Connect Existing
        </CustomButton> */}
        <CustomButton
          bgColor="#556bb8"
          color="#fff"
          height="40px"
          onClick={() => {
            if (
              !rolesAndPermissionStore.hasManagePermission(
                "Apply for Merchant Account"
              )
            ) {
              return;
            }
            navigate("/app/merchant-accounts/create-new");
          }}
        >
          Apply Now
        </CustomButton>
      </FlexContainer>
    );
  }

  return (
    <DashboardGrid>
      <Routes>
        <Route
          path={`/`}
          element={topBar(
            <MerchantAccounts
              merchantAccountStore={merchantAccountStore}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />,
            topRightButton(),
            !merchantAccountStore.isMerchantAccountsLoadingInProgress &&
              merchantAccountStore.merchantAccounts.length === 0
          )}
        />
        <Route
          path={`/no-merchant`}
          element={
            <div className="full-height merchant-account-plh">
              <NoMerchantAccountPlaceholder
                rolesAndPermissionStore={rolesAndPermissionStore}
              />
            </div>
          }
        />
        <Route
          path={`/create-new`}
          element={topBar(
            <MerchantAccountSignUp
              merchantAccountStore={merchantAccountStore}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />,
            <></>
          )}
        />
        <Route
          path={`/success/:id`}
          element={topBar(
            <MerchantApplicationSuccess
              merchantAccountStore={merchantAccountStore}
              id={merchantAccountId()}
              rolesAndPermissionStore={rolesAndPermissionStore}
              getMerchantAccounts={() => getMerchantAccounts()}
            />,
            topRightButton()
          )}
        />
        <Route
          path={`/details/:id`}
          element={
            <MerchantAccountDetail
              merchantAccountStore={merchantAccountStore}
              id={merchantAccountId()}
              rolesAndPermissionStore={rolesAndPermissionStore}
              itemStore={itemStore}
            />
          }
        />
        <Route
          path="*"
          element={<Navigate to="/merchant-accounts/create-new" />}
        />
      </Routes>
    </DashboardGrid>
  );
};

export default withRouter(observer(MerchantAccountRoutes));
